import { defineStore } from 'pinia';
import { useLiveAnnouncer } from '~/composables/useLiveAnnouncer';

/**
 * The app-level polite live region, for a status message whose own element arrives with it: a failure that mounts
 * already holding its sentence. A region that is on the page before its text changes (a list's result count, a loader)
 * announces for itself and does not need this one.
 *
 * A read that fails shows its failure by mounting it, and a live region mounted already holding its sentence is not
 * reliably read out (DESIGN.md, the form field's error element). This region is in the document from the app's first
 * render (`StatusAnnouncer`, in app.vue), so every message is a change to text already there, which is what a screen
 * reader reads out.
 *
 * It clears and re-sets rather than assigning (useLiveAnnouncer), so a second identical failure is still a change and
 * is still heard. Nuxt's own announcer assigns in one write, which would leave a repeat failure silent.
 */
export const useAnnouncerStore = defineStore('announcer', () => {
    const { announcement, announce: write } = useLiveAnnouncer();

    // The message most recently asked for, which the region shows a tick later.
    let requested = '';

    const announce = (message: string): Promise<void> => {
        requested = message;
        return write(message);
    };

    // Takes a message back when what said it has left the screen, unless something newer has been said since: the
    // region holds only what is still true.
    const withdraw = (message: string): void => {
        if (message && requested === message) void announce('');
    };

    return { announcement, announce, withdraw };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useAnnouncerStore, import.meta.hot));
}
